import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Divider } from "@material-ui/core";
import { savePaymentMethod } from "../redux/restaurantCartSlice";
import { resetOrderCreation } from "../redux/restaurantOrderActions";
import { PAYMENT_METHODS } from "../config/market";
import paymentBackgroundStyle from "./paymentBackground";
import styled from "styled-components";
import RestaurantNavBar from "../components/RestaurantNavBar";
import CheckoutStepper from "../components/CheckoutStepper";
import useRestaurantAvailability from "../hooks/useRestaurantAvailability";
import { availabilityAllowsOrdering } from "../utils/restaurantAvailability";
import RestaurantAvailabilityBanner from "../components/RestaurantAvailabilityBanner";
import { resolveProductPaymentPolicy } from "../utils/productPaymentPolicy";
import useCodPolicy from "../hooks/useCodPolicy";

const PaymentOptionButton = styled.button`
  border: none;
  padding: 15px 20px;
  background-color: teal;
  color: white;
  cursor: pointer;
  margin-top: 10px;
  border-radius: 4px;
  &:disabled {
    background-color: #ccc;
    color: #aaa;
    font-weight: 300;
    font-size: 14px;
    cursor: not-allowed;
  }
`;

const RestaurantPaymentScreen = () => {
    const dispatch = useDispatch();
    const history = useHistory();

    const currentRestaurant = useSelector((state) => state.restaurantCart.currentRestaurant);
    const cart = useSelector((state) => state.restaurantCart.carts[currentRestaurant] || {});
    const products = Object.values(cart);
    const outletId =
        products.find((item) => item?.outletId)?.outletId || null;
    const availability = useRestaurantAvailability({
        vendorId: currentRestaurant,
        outletId,
    });

    const [selectedOption, setSelectedOption] = useState(-1);

    useEffect(() => {
        if (products.length === 0) {
            // NEVER push /restaurant/:id here — that path is the MOBILE
            // deep-link route (DeepLinkHandler → "Opening Play Store…"), and
            // with a lost vendor pointer it literally became /restaurant/null.
            // An empty/expired checkout goes back to the cart hub instead.
            history.push("/cart");
        }
    }, [history, products, currentRestaurant]);

    // const submitHandler = (e) => {
    //     e.preventDefault();
    //     const options = ["MOMO", "Airtel", "Visa", "COD"];
    //     dispatch(savePaymentMethod(options[selectedOption]));
    //     history.push("/placeorder");
    // };

    const submitHandler = async (e) => {
        e.preventDefault();
        const latest = await availability.refresh();
        if (!availabilityAllowsOrdering(latest)) return;
        // Method keys come from the ACTIVE market registry (config/market.js):
        // UG unchanged; ZM adds ZAMTEL + FW-routed mobile money.
        dispatch(savePaymentMethod(paymentOptions[selectedOption].key));
        dispatch(resetOrderCreation());
        history.push(`/restaurant-placeorder/${currentRestaurant}`);
    };

    const handleOptionChange = (index) => {
        if (!availability.canOrder) return;
        setSelectedOption(index);
    };

    /* The live platform COD ceiling. Until it has loaded this is null and
       the policy below leaves Cash on delivery exactly where it was — see
       resolveProductPaymentPolicy for why a shipped constant would be worse
       than no ceiling at all. */
    const { maxProductAmount: codMaxProductAmount } = useCodPolicy();
    const paymentPolicy = resolveProductPaymentPolicy(products, PAYMENT_METHODS, {
      codMaxProductAmount,
    });
    const paymentOptions = paymentPolicy.options;

    useEffect(() => {
        if (selectedOption >= paymentOptions.length) setSelectedOption(-1);
        if (selectedOption === -1 && paymentOptions.length === 1) setSelectedOption(0);
    }, [paymentOptions.length, selectedOption]);

    const optionStyle = (isSelected) => ({
        display: "flex",
        alignItems: "center",
        gap: "16px",
        padding: "12px 16px",
        borderRadius: "4px",
        cursor: "pointer",
        backgroundColor: isSelected ? "#DBF3FA" : "#EDF2F7",
        boxShadow: isSelected ? "0px 2px 5px rgba(0, 0, 0, 0.1)" : "none",
        transition: "all 0.3s",
        textAlign: "center",
        fontWeight: isSelected ? "bold" : "normal",
        marginBottom: "16px",
        height: "60px",
    });

    return (
        <>
            <RestaurantNavBar />
            <div
                style={{
                    width: "100vw",
                    height: "100vh",
                    ...paymentBackgroundStyle,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginTop: "30px",
                }}
            >
                <div
                    style={{
                        maxWidth: "500px",
                        width: "80%",
                        padding: "20px",
                        backgroundColor: "white",
                        borderRadius: "10px",
                        boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.9)",
                        marginTop: "30px"
                    }}
                >
                    <CheckoutStepper currentStep="payment" />
                    <h1
                        style={{ fontSize: "24px", fontWeight: "300", textAlign: "center" }}
                    >
                        PAYMENT OPTIONS
                    </h1>
                    <Divider />
                    <form>
                        <RestaurantAvailabilityBanner availability={availability} />
                        <label
                            style={{
                                display: "block",
                                marginBottom: "10px",
                                marginTop: "20px",
                                fontSize: "16px",
                                fontStyle: "italic",
                                color: "#4A4A4A",
                            }}
                        >
                            Select Payment Method
                        </label>
                        {paymentPolicy.notice && (
                            <div style={{ background: "#fff7ed", border: "1px solid #fdba74", color: "#9a3412", borderRadius: 6, padding: "8px 12px", marginBottom: 12 }}>
                                ⚠️ {paymentPolicy.notice}
                            </div>
                        )}
                        {paymentOptions.map(({ label, imageUrl }, index) => (
                            <div
                                key={index}
                                style={optionStyle(selectedOption === index)}
                                onClick={() => handleOptionChange(index)}
                            >
                                {imageUrl ? (
                                    <img
                                        src={imageUrl}
                                        alt={label}
                                        style={{ width: "50px", height: "44px", marginRight: "5px" }}
                                    />
                                ) : (
                                    <div style={{ width: "50px", height: "44px", marginRight: "5px" }} />
                                )}
                                <div
                                    style={{
                                        width: "16px",
                                        height: "16px",
                                        borderRadius: "50%",
                                        border:
                                            selectedOption === index
                                                ? "3px solid #3f83f8"
                                                : "1px solid #D1D5DB",
                                        backgroundColor:
                                            selectedOption === index ? "#3f83f8" : "transparent",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        marginRight: "8px",
                                    }}
                                >
                                    {selectedOption === index && (
                                        <div
                                            style={{
                                                width: "8px",
                                                height: "8px",
                                                borderRadius: "50%",
                                                backgroundColor: "white",
                                            }}
                                        ></div>
                                    )}
                                </div>
                                {label}
                            </div>
                        ))}
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "flex-end",
                            }}
                        >
                            <PaymentOptionButton
                                type="button"
                                variant="contained"
                                color="primary"
                                disabled={selectedOption === -1 || !availability.canOrder}
                                style={{
                                    width: "150px",
                                    marginTop: "10px",
                                }}
                                onClick={submitHandler}
                            >
                                {!availability.canOrder
                                    ? availability.isClosed
                                        ? "Restaurant closed"
                                        : availability.isUnknown
                                            ? "Hours unavailable"
                                            : "Checking hours…"
                                    : "Continue"}
                            </PaymentOptionButton>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default RestaurantPaymentScreen;
